import type { Ref } from "vue"
import { ElLink, ElMessage, ElMessageBox, ElNotification } from "element-plus"
import { cloneDeep, difference, intersection, union } from "lodash-es"
import { h, ref } from "vue"
import { NoPermissionPageName } from "@/constants"
import {
  notifyLocalPrintTasksReady,
  parseLocalPrintTaskNotificationPayload,
} from "@/features/printing/localPrintTaskPoller"
import router from "@/router"
import { useAppStore } from "@/store/modules/app"
import { useUserStore } from "@/store/modules/user"
import { useViewScopeStore } from "@/store/modules/viewScope"
import { MessageType } from "@/types/messageType"
import { SystemTypeConstants } from "@/types/platformType"
import { getComponentName } from "@/utils"
import { recordBusinessContextTrace } from "@/utils/businessContext/diagnostics"
import { createBusinessContextRuntimeGuard } from "@/utils/businessContext/epoch"
import {
  applySystemVersionNotice,
  loadSystemVersionForOrganizationScope,
} from "@/utils/businessContext/systemVersion"
import { getToken } from "@/utils/cache/cookies"
import { getExtPermissionsByPath, getPermissionsByPath } from "@/utils/permission"
import { setSenderId } from "@/utils/sender-id"

function parseSystemVersionNotice(data: unknown) {
  try {
    const parsed = typeof data === "string" ? JSON.parse(data) : data
    if (
      !parsed
      || (parsed.noticeType !== 1 && parsed.noticeType !== 2)
    ) {
      return undefined
    }
    return parsed as { noticeType: 1 | 2, systemVersionId: number }
  } catch (error) {
    console.warn("系统版本通知格式无效:", error)
  }
}

function reenterPermissionAwareRoute() {
  router.replace({
    path: "/",
    replace: true,
  }).catch((error) => {
    console.warn("权限刷新后重新进入可用页面失败:", error)
  })
}

export default function useWebSocket(
  url: string,
  onMessage: any,
  onError: any,
  onClose: any,
  logout: any,
  maxRetries = 30,
) {
  const socket: Ref<WebSocket | null> = ref(null)
  const isOpen: Ref<boolean> = ref(false)
  const retries = ref(0)
  // 新增：保存上次的 url
  let lastUrl: string | undefined
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined
  let manuallyDisconnected = false

  const clearReconnectTimer = () => {
    if (reconnectTimer !== undefined) {
      clearTimeout(reconnectTimer)
      reconnectTimer = undefined
    }
  }

  const isSocketOpen = (target: WebSocket | null = socket.value) => {
    return target?.readyState === WebSocket.OPEN
  }

  const safeSend = (message: string, target: WebSocket | null = socket.value) => {
    try {
      if (isSocketOpen(target)) {
        target?.send(message)
        return true
      }
    } catch (error) {
      console.error("Failed to send WebSocket message:", error)
      onError(error)
    }
    return false
  }

  // 配置心跳检测
  const heartCheck = {
    timeout: 60000,
    timeoutObj: null as any,
    reset() {
      clearTimeout(this.timeoutObj)
      this.start()
    },
    start() {
      this.timeoutObj = setTimeout(() => {
        safeSend("ping")
      }, this.timeout)
    },
    stop() {
      clearTimeout(this.timeoutObj)
      this.timeoutObj = null
    },
  }

  const connect = (customUrl?: string) => {
    try {
      clearReconnectTimer()
      manuallyDisconnected = false
      if (retries.value >= maxRetries) {
        console.error(`Failed to connect after ${maxRetries} attempts. Stopping retries.`)
        return
      }

      // 支持自定义 url，便于 resetWebSocket 时传递新参数
      const wsUrl = customUrl || `${url}/teashop@${getToken()}?gray=${useAppStore().getIsGray()}`
      lastUrl = wsUrl
      const currentSocket = new WebSocket(wsUrl)
      socket.value = currentSocket
      currentSocket.onopen = () => {
        if (socket.value !== currentSocket) {
          return
        }
        isOpen.value = true
        retries.value = 0
        heartCheck.reset()
        safeSend("ping", currentSocket)
        // 连接恢复后补查持久任务，并刷新可能错过变更通知的设备配置。
        notifyLocalPrintTasksReady({ count: 0 })
      }

      currentSocket.onmessage = (event) => {
        if (socket.value !== currentSocket) {
          return
        }
        if (event.data == null) {
          return
        }

        if (event.data.includes("pong") || event.data.includes("ping")) {
          if (event.data === "pong") {
            heartCheck.reset()
          }
          if (event.data === "ping") {
            safeSend("pong", currentSocket)
          }
        } else {
          let message: any
          try {
            message = JSON.parse(event.data)
          } catch (error) {
            console.error("Failed to parse WebSocket message:", error, event.data)
            onMessage(event)
            return
          }
          if (message.type === MessageType.VersionNotice && Number(message.target) === SystemTypeConstants.B_CASHIER) {
            const messageData = parseSystemVersionNotice(message.data)
            if (messageData) {
              const viewScopeStore = useViewScopeStore()
              const startedScopeId = viewScopeStore.activeScopeId
              const contextGuard = createBusinessContextRuntimeGuard({
                getToken,
                getScopeId: () => viewScopeStore.activeScopeId,
              })
              loadSystemVersionForOrganizationScope(
                viewScopeStore.activeScope,
                startedScopeId,
                contextGuard,
              ).then(({ version }) => {
                const result = applySystemVersionNotice(
                  version,
                  messageData.noticeType,
                  contextGuard,
                )
                if (result.status !== "completed" || !result.version?.systemVersion) {
                  return
                }
                const newSystemVersion = result.version.systemVersion.systemVersion
                if (result.updateType === 2) {
                  useAppStore().handleUpdateMessage()
                } else if (result.updateType === 1) {
                  ElNotification({
                    title: `产品已更新至最新版本(${newSystemVersion})`,
                    duration: 0,
                    message: h("div", null, [
                      "我们修复了一些已知问题，并优化了性能。请刷新页面进行使用",
                      h(ElLink, {
                        type: "primary",
                        underline: "always",
                        href: "#",
                        style: "margin-top: -4px;", // TODO Hacky 目的为了对齐文本，需要优化
                        onClick: (event: Event) => {
                          event.preventDefault()
                          useAppStore().handleUpdateMessage()
                        },
                      }, "查看更新日志"),
                    ]),
                  })
                }
              }).catch((error) => {
                console.warn("处理系统版本通知失败:", error)
              })
            }
          }
          if (message.type === MessageType.LoginRepeat) {
            socket.value?.close()
            retries.value = 5
            heartCheck.stop()
            logout()
            ElMessage.error(message.data)
            router.push("/login")
          }
          if (message.type === MessageType.WebsocketIdentity) {
            setSenderId(message.data ?? "")
          }
          if (message.type === MessageType.LocalPrintTaskCreated) {
            const messageData = parseLocalPrintTaskNotificationPayload(message.data)
            if (messageData) {
              notifyLocalPrintTasksReady(messageData)
            }
          }
          if (message.type === MessageType.UserDeleted) {
            ElMessage.error('当前账号已被删除')
            logout()
            return
          }
          if (
            message.type === MessageType.RefreshShopLoginStatus
            && !useViewScopeStore().contextSwitching
          ) {
            const startedScopeId = useViewScopeStore().activeScopeId
            recordBusinessContextTrace("websocket.permission-refresh-started", {
              startedScopeId,
              route: router.currentRoute.value.fullPath,
            })
            const loginPermissions = ["login", "login_pc"]
            // 1. 保存当前用户权限信息
            const oldPermissionList = cloneDeep(useUserStore().getPermissionList())
            // 2. 获取最新的用户权限信息
            useUserStore()
              .refreshTokenRequest()
              .then(() => {
                recordBusinessContextTrace("websocket.permission-refresh-completed", {
                  startedScopeId,
                  activeScopeId: useViewScopeStore().activeScopeId,
                  route: router.currentRoute.value.fullPath,
                })
                if (useViewScopeStore().activeScopeId !== startedScopeId) {
                  return
                }
                const newPermissionList = useUserStore().getPermissionList()
                // 3. 找到变更的用户权限
                // 3-1. 找到移除的用户权限
                const removePermissions = oldPermissionList.filter(
                  (oldPermission) => !newPermissionList.includes(oldPermission),
                )
                // 3-2. 找到新增的用户权限
                const addPermissions = newPermissionList.filter(
                  (newPermission) => !oldPermissionList.includes(newPermission),
                )
                // 4. 获取当前界面以及父界面所需要的权限
                const currentPath = router.currentRoute.value.path
                // 获取当前路由对应的组件
                const currentRoute = router.currentRoute.value
                // 数组的最后一项代表最深层、最具体的匹配路由。它通常对应于当前页面的主要组件
                const currentComponent = currentRoute.matched[currentRoute.matched.length - 1]?.components?.default
                const is403Page = getComponentName(currentComponent) === NoPermissionPageName
                const routes = router.getRoutes()
                const currentPathPermissions = getPermissionsByPath(routes, currentPath)
                const extPermissions = getExtPermissionsByPath(routes, currentPath)
                // 5. 如果当前界面的权限受到影响，则进行界面的刷新模式判断 force会提示 force_auto不会提示
                let refreshType: "silent" | "logout" | "force" | "force_auto" = "silent"
                // 所有必要的权限 key
                const allRequiredPermissions = union(
                  currentPathPermissions,
                  extPermissions
                    .filter((permission) => permission.refresh === "force")
                    .map((permission) => permission.key),
                )
                // 所有的额外可选权限 key
                const allOptionalPermissions = extPermissions
                  .filter((permission) => permission.refresh === "silent")
                  .map((permission) => permission.key)

                // 被移除的必要权限
                const removedRequiredIntersectionPermissions = intersection(removePermissions, allRequiredPermissions)
                // 被添加的可选权限
                const addedOptionalIntersectionPermissions = intersection(addPermissions, allOptionalPermissions)
                // 必要权限是否足够
                const allRequiredPermissionsIsEnough =
                  difference(allRequiredPermissions, newPermissionList).length === 0
                // 登录权限是否足够
                const loginPermissionsIsEnough = difference(loginPermissions, newPermissionList).length === 0
                if (!loginPermissionsIsEnough) {
                  refreshType = "logout"
                } else if (is403Page) {
                  if (allRequiredPermissionsIsEnough) {
                    refreshType = "force_auto"
                  } else {
                    refreshType = "silent"
                  }
                } else {
                  if (removedRequiredIntersectionPermissions.length > 0) {
                    refreshType = "force"
                  }
                  if (addedOptionalIntersectionPermissions.length > 0) {
                    refreshType = "silent"
                  }
                }
                // 6. 执行刷新
                if (refreshType === "logout") {
                  ElMessageBox.alert("您暂无PC端的登录权限，将自动退出系统", "提示", {
                    confirmButtonText: "确定",
                    callback: () => {
                      logout()
                      router.push("/login")
                    },
                  })
                } else if (refreshType === "force") {
                  ElMessageBox.alert("当前页面权限发生变化，页面将自动刷新", "提示", {
                    confirmButtonText: "确定",
                    callback: () => {
                      reenterPermissionAwareRoute()
                    },
                  })
                } else if (refreshType === "force_auto") {
                  reenterPermissionAwareRoute()
                }
              })
              .catch((error) => {
                recordBusinessContextTrace("websocket.permission-refresh-failed", {
                  startedScopeId,
                  activeScopeId: useViewScopeStore().activeScopeId,
                  error,
                })
                console.warn("权限变更后的业务上下文刷新失败:", error)
              })
          }
        }
        onMessage(event)
      }

      currentSocket.onerror = (event) => {
        if (socket.value !== currentSocket) {
          return
        }
        onError(event)
        console.error(event)
        if (currentSocket.readyState === WebSocket.OPEN || currentSocket.readyState === WebSocket.CONNECTING) {
          currentSocket.close()
        }
      }

      currentSocket.onclose = (event) => {
        if (socket.value !== currentSocket) {
          return
        }
        isOpen.value = false
        heartCheck.stop()
        socket.value = null
        retries.value++
        if (!manuallyDisconnected) {
          reconnectTimer = setTimeout(() => {
            reconnectTimer = undefined
            connect()
          }, 1000 * retries.value)
        }
        onClose(event)
      }
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error)
      onError(error)
    }
  }

  function closeCurrentSocket() {
    if (socket.value) {
      try {
        socket.value.onopen = null
        socket.value.onmessage = null
        socket.value.onerror = null
        socket.value.onclose = null
        socket.value.close()
      } catch (_e) {
        // 忽略关闭异常
      }
      socket.value = null
    }
    isOpen.value = false
    heartCheck.stop()
  }

  /**
   * 重新创建 WebSocket 连接，彻底销毁旧连接，支持参数变更
   * @param newUrl 可选，新的 WebSocket 连接地址（如 gray 状态变化时）
   */
  function resetWebSocket(newUrl?: string) {
    // 计算本次 url
    const wsUrl = newUrl || `${url}/teashop@${getToken()}?gray=${useAppStore().getIsGray()}`
    // 只有 url 变化时才真正重建
    if (wsUrl !== lastUrl) {
      clearReconnectTimer()
      manuallyDisconnected = false
      closeCurrentSocket()
      retries.value = 0
      connect(newUrl)
    }
  }

  function disconnect() {
    manuallyDisconnected = true
    clearReconnectTimer()
    closeCurrentSocket()
    retries.value = 0
    lastUrl = undefined
  }

  const send = (message: string) => {
    safeSend(message)
  }

  return {
    socket,
    isOpen,
    send,
    connect,
    resetWebSocket, // 新增暴露
    disconnect,
  }
}
